import { cn } from '../../../cn';
import { type ReactNode, useCallback, useRef, useState } from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FileMetadata = Record<string, any>;
export type UploaderFile = File & FileMetadata;

export type UploaderProps = {
  disabled?: boolean;
  enableDragDrop?: boolean;
  accept?: string;
  multiple?: boolean;
  onChange?: (files: UploaderFile[]) => void;
  children?: ReactNode | ((props: { isDragOver: boolean }) => ReactNode);
  className?: string;
  onEachFile?: (file: File) => FileMetadata | Promise<FileMetadata>;
  beforeUpload?: (files: File[]) => boolean;
};

export function Uploader({
  disabled,
  enableDragDrop,
  accept,
  multiple,
  onChange,
  children,
  className,
  onEachFile,
  beforeUpload,
}: UploaderProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounter = useRef(0);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = useCallback(() => {
    if (disabled) {
      return;
    }
    inputRef.current?.click();
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      dragCounter.current = 0;

      if (disabled || !enableDragDrop) {
        return;
      }

      if (!e.dataTransfer.files) {
        return;
      }
      const files = Array.from(e.dataTransfer.files);

      const canProcessNext = beforeUpload ? beforeUpload?.(files) : true;

      if (!canProcessNext) {
        return;
      }

      const uploadedFiles: UploaderFile[] = [];

      for (const file of files) {
        const metadata = onEachFile ? await onEachFile(file) : {};
        uploadedFiles.push(Object.assign(file, metadata));
      }

      onChange?.(uploadedFiles);
    },
    [disabled, enableDragDrop, beforeUpload, onChange, onEachFile],
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled || !enableDragDrop) {
        return;
      }
      dragCounter.current++;
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        setIsDragOver(true);
      }
    },
    [disabled, enableDragDrop],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  const handleInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files) {
        return;
      }

      const files = Array.from(e.target.files);

      const canProcessNext = beforeUpload ? beforeUpload?.(files) : true;

      if (!canProcessNext) {
        return;
      }

      const uploadedFiles: UploaderFile[] = [];

      for (const file of files) {
        const metadata = onEachFile ? await onEachFile(file) : {};
        uploadedFiles.push(Object.assign(file, metadata));
      }

      onChange?.(uploadedFiles);
      // Reset input value to allow selecting the same file again
      e.target.value = '';
    },
    [beforeUpload, onChange, onEachFile],
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      disabled={disabled}
      className={cn(
        'relative flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 transition-colors cursor-pointer',
        'hover:bg-muted/50 focus:outline-none',
        isDragOver && 'border-primary bg-primary/5',
        disabled && 'cursor-not-allowed opacity-50 hover:bg-transparent',
        !isDragOver && !disabled && 'border-muted-foreground/25',
        className,
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleInputChange}
        disabled={disabled}
        className="sr-only"
        aria-label="File upload"
      />
      <div>
        {typeof children === 'function' ? children({ isDragOver }) : children}
      </div>
    </button>
  );
}
